import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CircleSelectionConfig } from '../model/map/circle-selection-config';
import { MapDrawType } from '../model/map/map-draw-type';

/**
 * Service to manage Circle Selection Tool state
 * Handles configuration storage, placement mode tracking, and created circles
 */
@Injectable({
    providedIn: 'root'
})
export class CircleSelectionStateService {
    
    // Configuration state
    private configSubject = new BehaviorSubject<CircleSelectionConfig | null>(null);
    public config$: Observable<CircleSelectionConfig | null> = this.configSubject.asObservable();
    
    // Placement mode state
    private placementModeSubject = new BehaviorSubject<boolean>(false);
    public placementMode$: Observable<boolean> = this.placementModeSubject.asObservable();

    // Draw-mode state for the in-house capture tools.
    //
    // Each subject carries the DRAW TARGET rather than a bare flag: MapDrawType.FILTER
    // for a Select shape that filters datapoints, MapDrawType.MEASURE for a Measure
    // shape that only reports its area or distance. null means the tool is disarmed.
    // The capture code is identical for both; only the colour and the array the shape
    // lands in differ.

    // "Rectangle New" / Measure > Rectangle - drag one corner to the opposite one
    private rectangleDrawModeSubject = new BehaviorSubject<MapDrawType | null>(null);
    public rectangleDrawMode$: Observable<MapDrawType | null> = this.rectangleDrawModeSubject.asObservable();

    // "Polygon New" / Measure > Polygon - click to add each vertex
    private polygonDrawModeSubject = new BehaviorSubject<MapDrawType | null>(null);
    public polygonDrawMode$: Observable<MapDrawType | null> = this.polygonDrawModeSubject.asObservable();

    // Measure > Distance - click to add each point, left open rather than closed
    private polylineDrawModeSubject = new BehaviorSubject<MapDrawType | null>(null);
    public polylineDrawMode$: Observable<MapDrawType | null> = this.polylineDrawModeSubject.asObservable();

    // Measure > Circle - drag out from the centre to set the radius
    private circleDragDrawModeSubject = new BehaviorSubject<MapDrawType | null>(null);
    public circleDragDrawMode$: Observable<MapDrawType | null> = this.circleDragDrawModeSubject.asObservable();

    // Active circle reference (for move/resize operations)
    private activeCircleSubject = new BehaviorSubject<google.maps.Circle | null>(null);
    public activeCircle$: Observable<google.maps.Circle | null> = this.activeCircleSubject.asObservable();
    
    // Circle center marker reference
    private centerMarkerSubject = new BehaviorSubject<google.maps.Marker | null>(null);
    public centerMarker$: Observable<google.maps.Marker | null> = this.centerMarkerSubject.asObservable();
    
    constructor() { }
    
    /**
     * Set the circle configuration
     * @param config - Circle configuration with radius value, unit, and meters
     */
    setConfig(config: CircleSelectionConfig): void {
        this.configSubject.next(config);
    }
    
    /**
     * Get the current configuration value (synchronous)
     * @returns Current configuration or null
     */
    getConfigValue(): CircleSelectionConfig | null {
        return this.configSubject.value;
    }
    
    /**
     * Clear the configuration
     */
    clearConfig(): void {
        this.configSubject.next(null);
    }
    
    /**
     * Disarm every draw/placement mode except the one being entered.
     *
     * The tools each install their own map listeners, so two armed at once would
     * both react to the same mousedown and draw two shapes. Selecting any tool
     * therefore cancels whichever was armed before it.
     */
    private exitOtherModes(
        keep: 'placement' | 'rectangle' | 'polygon' | 'polyline' | 'circleDrag' | 'none'
    ): void {
        if (keep !== 'placement' && this.placementModeSubject.value) {
            this.placementModeSubject.next(false);
        }
        if (keep !== 'rectangle' && this.rectangleDrawModeSubject.value !== null) {
            this.rectangleDrawModeSubject.next(null);
        }
        if (keep !== 'polygon' && this.polygonDrawModeSubject.value !== null) {
            this.polygonDrawModeSubject.next(null);
        }
        if (keep !== 'polyline' && this.polylineDrawModeSubject.value !== null) {
            this.polylineDrawModeSubject.next(null);
        }
        if (keep !== 'circleDrag' && this.circleDragDrawModeSubject.value !== null) {
            this.circleDragDrawModeSubject.next(null);
        }
    }

    /**
     * Disarm every draw/placement tool.
     *
     * This is the escape hatch: the Select panel's Cancel button routes here, so
     * there is always a way out of a draw even if a tool's own finish gesture
     * does not land.
     */
    exitAllDrawModes(): void {
        this.exitOtherModes('none');
    }

    /**
     * Enter placement mode (waiting for user to click map)
     */
    enterPlacementMode(): void {
        this.exitOtherModes('placement');
        this.placementModeSubject.next(true);
    }
    
    /**
     * Exit placement mode
     */
    exitPlacementMode(): void {
        this.placementModeSubject.next(false);
    }
    
    /**
     * Get the current placement mode value (synchronous)
     * @returns True if in placement mode, false otherwise
     */
    isInPlacementMode(): boolean {
        return this.placementModeSubject.value;
    }
    /**
     * Arm rectangle capture - drag one corner to the opposite one.
     * @param target FILTER for a Select shape, MEASURE for a Measure shape
     */
    enterRectangleDrawMode(target: MapDrawType = MapDrawType.FILTER): void {
        this.exitOtherModes('rectangle');
        this.rectangleDrawModeSubject.next(target);
    }

    /**
     * Disarm rectangle capture
     */
    exitRectangleDrawMode(): void {
        this.rectangleDrawModeSubject.next(null);
    }

    /**
     * Get the current rectangle draw target (synchronous), or null when disarmed
     */
    getRectangleDrawTarget(): MapDrawType | null {
        return this.rectangleDrawModeSubject.value;
    }

    /**
     * Arm polygon capture - click once per vertex, then close the ring.
     * @param target FILTER for a Select shape, MEASURE for a Measure shape
     */
    enterPolygonDrawMode(target: MapDrawType = MapDrawType.FILTER): void {
        this.exitOtherModes('polygon');
        this.polygonDrawModeSubject.next(target);
    }

    /**
     * Disarm polygon capture
     */
    exitPolygonDrawMode(): void {
        this.polygonDrawModeSubject.next(null);
    }

    /**
     * Get the current polygon draw target (synchronous), or null when disarmed
     */
    getPolygonDrawTarget(): MapDrawType | null {
        return this.polygonDrawModeSubject.value;
    }

    /**
     * Arm polyline capture - click once per point, left open rather than closed.
     * Only used by Measure > Distance; there is no polyline filter shape.
     */
    enterPolylineDrawMode(target: MapDrawType = MapDrawType.MEASURE): void {
        this.exitOtherModes('polyline');
        this.polylineDrawModeSubject.next(target);
    }

    /**
     * Disarm polyline capture
     */
    exitPolylineDrawMode(): void {
        this.polylineDrawModeSubject.next(null);
    }

    /**
     * Get the current polyline draw target (synchronous), or null when disarmed
     */
    getPolylineDrawTarget(): MapDrawType | null {
        return this.polylineDrawModeSubject.value;
    }

    /**
     * Arm circle drag capture - press at the centre and drag out to set the radius.
     * Used by Measure > Circle, where the radius is the thing being measured and so
     * cannot be typed in up front the way the Select panel's Circle tool does it.
     */
    enterCircleDragDrawMode(target: MapDrawType = MapDrawType.MEASURE): void {
        this.exitOtherModes('circleDrag');
        this.circleDragDrawModeSubject.next(target);
    }

    /**
     * Disarm circle drag capture
     */
    exitCircleDragDrawMode(): void {
        this.circleDragDrawModeSubject.next(null);
    }

    /**
     * Get the current circle drag draw target (synchronous), or null when disarmed
     */
    getCircleDragDrawTarget(): MapDrawType | null {
        return this.circleDragDrawModeSubject.value;
    }

    /**
     * Set the active circle reference
     * @param circle - Google Maps Circle instance
     */
    setActiveCircle(circle: google.maps.Circle | null): void {
        this.activeCircleSubject.next(circle);
    }
    
    /**
     * Get the active circle reference (synchronous)
     * @returns Active circle or null
     */
    getActiveCircle(): google.maps.Circle | null {
        return this.activeCircleSubject.value;
    }
    
    /**
     * Set the center marker reference
     * @param marker - Google Maps Marker instance
     */
    setCenterMarker(marker: google.maps.Marker | null): void {
        this.centerMarkerSubject.next(marker);
    }
    
    /**
     * Get the center marker reference (synchronous)
     * @returns Center marker or null
     */
    getCenterMarker(): google.maps.Marker | null {
        return this.centerMarkerSubject.value;
    }
    
    /**
     * Clear all state (config, placement mode, active circle, marker)
     * Used when canceling or resetting the tool
     */
    clearAll(): void {
        this.clearConfig();
        this.exitPlacementMode();
        
        // Remove circle from map if exists
        const circle = this.getActiveCircle();
        if (circle) {
            circle.setMap(null);
        }
        this.setActiveCircle(null);
        
        // Remove marker from map if exists
        const marker = this.getCenterMarker();
        if (marker) {
            marker.setMap(null);
        }
        this.setCenterMarker(null);
    }
    
    /**
     * Get a summary of the current state (for debugging)
     * @returns State summary object
     */
    getStateSummary(): {
        hasConfig: boolean;
        isInPlacementMode: boolean;
        hasActiveCircle: boolean;
        hasCenterMarker: boolean;
        config: CircleSelectionConfig | null;
    } {
        return {
            hasConfig: this.configSubject.value !== null,
            isInPlacementMode: this.placementModeSubject.value,
            hasActiveCircle: this.activeCircleSubject.value !== null,
            hasCenterMarker: this.centerMarkerSubject.value !== null,
            config: this.configSubject.value
        };
    }
}
